<template>
  <div ref="app" class="browser h-full w-full">
    <NGrid class="h-full pt-4 overflow-hidden" cols="6" item-responsive>
      <NGridItem class="overflow-auto" span="6">
        <FileSmartFolders v-if="isSmartFoldersView">
          <template v-if="slots['single-file-details']" #single-file-details>
            <slot name="single-file-details" />
          </template>
        </FileSmartFolders>
        <Content v-else>
          <template v-if="slots['single-file-details']" #single-file-details>
            <slot name="single-file-details" />
          </template>
        </Content>
      </NGridItem>
    </NGrid>
    <template v-if="appName === 'admin'">
      <CUpsertFolderModal
        v-if="['create-folder', 'update-folder'].includes(currentState)"
        :initial-values="currentState === 'update-folder' ? { name: updatingFolder?.name ?? '' } : undefined"
        @submit="(name) => onUpsertFolder(name, updatingFolder?.name)"
        @update:show="setCurrentState('browse')"
      />
      <CBatchMoveItemsModal
        v-if="currentState === 'batch-move'"
        v-model:show="isBatchMoveModalVisible"
        :current-folder="data.currentFolder"
        :current-folder-id="currentFolderId"
        :folders="data.folders"
        :is-fetching-level="data.isFetchingLevel"
        :is-moving="state.isFetchingFolderItems"
        :path="data.path"
        :selected-item-ids="selectedItemIds"
        @fetch-level="fetchLevel"
        @move-items="moveItems"
      />
    </template>
    <template v-if="appName === 'admin' || (appName === 'impact' && launchDarkly?.allow_rep_file_upload)">
      <UploadFilesModal v-if="currentState === 'upload-files'" />
      <UploadFilesDropzone v-if="app" :area="app" />
      <UploadFilesWidget :uploads="uploadQueue" @clear="clearCompletedAndFailed" @remove="removeUpload" />
    </template>
    <DetailsDrawer v-if="isContentPanelOpen && isMobileView">
      <template #single-file-details>
        <slot v-if="!!(slots['single-file-details'] && selectedFileId)" name="single-file-details" />
      </template>
    </DetailsDrawer>
  </div>
</template>

<script setup lang="ts">
import { syncRef } from '@vueuse/core'
import { NGridItem, NGrid } from 'naive-ui'
import {
  useSlots,
  onBeforeUnmount,
  toRefs,
  type ComputedRef,
  inject,
  computed,
  provide,
  ref,
  reactive,
  watch,
  onMounted,
  onUnmounted,
} from 'vue'

import CUpsertFolderModal from '@lib/components/CUpsertFolderModal/CUpsertFolderModal.vue'
import DetailsDrawer from '@browser/components/Content/Panel/DetailsDrawer/DetailsDrawer.vue'
import Content from '@browser/components/Content/Content.vue'
import FileSmartFolders from '@browser/components/FileSmartFolders.vue'
import CBatchMoveItemsModal from '@lib/components/CBatchMoveItemsModal/CBatchMoveItemsModal.vue'
import UploadFilesModal from '@browser/components/UploadFilesModal/UploadFilesModal.vue'
import UploadFilesDropzone from '@browser/components/UploadFilesDropzone/UploadFilesDropzone.vue'
import UploadFilesWidget from '@browser/components/UploadFilesWidget/UploadFilesWidget.vue'
import useAppStore, { type BrowserSectionType } from '@browser/stores/app'
import useApiStore, { type ItemsMoverItem } from '@browser/stores/api'
import useUploadStore from '@browser/stores/upload'
import useFileSmartFolderStore from '@browser/stores/fileSmartFolderStore'
import useThemeVars from '@lib/composables/useThemeVars'
import useBulkActions from '@lib/composables/bulkActions.use'
import type { File, FileRetrieve, MiniFolder, MiniFile, FolderRetrieve } from '@openapi'
import useAppEmit from '@browser/composables/useAppEmit'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { BrowserAppProps } from '@browser/appTypes'
import type { Mutable } from '@lib/types/ts'
import type { InstanceSettings, OrganizationSettings } from '@lib/main.lib'
import { debounce } from 'lodash-es'

const emit = defineEmits<{
  generateKeypoints: [id: File['id']]
  viewFileDetails: [id: File['id']]
  onSectionChange: [params: { section: BrowserSectionType; folderId: string; newTab?: boolean }]
  addToSharebox: [
    fileShareboxItem: { fileId: FileRetrieve['id']; name: FileRetrieve['name']; type: 'file'; thumbnailUrl?: string },
  ]
  filesDeleted: [ids: MiniFile['id'][]]
}>()

useAppEmit(emit)

const app = ref<HTMLElement | undefined>()
const { isSmartFoldersView } = useFileSmartFolderStore()
const { deleteFiles, deleteFolders, deleteFavorites } = useBulkActions(emit)
const { isContentPanelOpen, selectedFileId, isMobileView } = useAppStore()

const props = withDefaults(defineProps<BrowserAppProps>(), {
  appName: 'impact',
})

provide(
  'appProps',
  computed(() => props),
)

const slots = useSlots()
const themeVars = useThemeVars()
const {
  currentState,
  displayedFiles,
  updatingFolder,
  setAppName,
  selectedItemIds,
  selectedSection,
  setSelectedSection,
  setCurrentState,
  selectedFolderId,
  pitcherSettings: appPitcherSettings,
} = useAppStore()
const {
  state,
  actions: {
    init,
    setFavorites,
    onUploadFilesRefetchFolder,
    reset: resetApiStore,
    setCurrentFolder,
    clearTimeouts,
    onUpsertFolder,
  },
} = useApiStore()
const { currentFolderId, folderItemsFetcher, recentFilesFetcher, favoritesFetcher } = toRefs(state)

const {
  actions: { clearCompletedAndFailed, removeUpload },
  state: { isQueueBusy, queue: uploadQueue },
} = useUploadStore()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

// Sync all pitcher settings to app store
syncRef(pitcherSettings, appPitcherSettings, { immediate: true, direction: 'ltr' })

const data = reactive({
  isFetchingLevel: false,
  currentFolder: {
    id: '',
    name: '',
  } as Mutable<MiniFolder>,
  folders: [] as MiniFolder[],
  path: [] as FolderRetrieve['path'],
})

const isBatchMoveModalVisible = computed({
  get: () => currentState.value === 'batch-move',
  set: (value) => !value && setCurrentState('browse'),
})

watch(currentState, (newState) => {
  if (newState === 'batch-move') {
    data.currentFolder = {
      id: currentFolderId.value,
      name: state.folders.find((f) => f.id === currentFolderId.value)?.name || '',
    }

    fetchLevel(currentFolderId.value)
  }
})

async function fetchLevel(folderId?: string | null) {
  try {
    data.isFetchingLevel = true
    const { folders, name, id, path } = (await state.folderItemsFetcher?.(folderId || undefined, {
      justFetch: true,
      setSelectedFolder: false,
    })) as FolderRetrieve
    data.folders = folders.filter((f: MiniFolder) => !selectedItemIds.value.includes(f.id)) ?? []
    data.currentFolder.name = name
    data.currentFolder.id = id
    data.path = path
  } catch (e) {
    data.folders = []
    data.currentFolder.name = ''
    data.currentFolder.id = ''
    data.path = []
  } finally {
    data.isFetchingLevel = false
  }
}

async function moveItems(targetFolderId: string | null, itemIds: string[]) {
  const getType = (id: MiniFile['id'] | MiniFolder['id']) => {
    if (state.folders.find((f) => f.id === id)) {
      return 'folder'
    }
    if (displayedFiles.value.find((f) => f.id === id)) {
      return 'file'
    }
    return null
  }

  try {
    await state.itemsMover?.(
      targetFolderId ?? '',
      itemIds.reduce((acc, id) => {
        const type = getType(id)
        type && acc.push({ id, type })
        return acc
      }, [] as ItemsMoverItem[]),
    )
    setCurrentState('browse')
  } catch (e) {
    console.error(e)
  }
}

function refreshSelectedFolderDetails(folderId?: string) {
  const folderToRefresh = folderId || selectedFolderId.value

  if (folderToRefresh) {
    return state.folderItemsFetcher?.(folderToRefresh, {
      justFetch: false,
      setSelectedFolder: true,
      forceRefresh: true,
    })
  }
  return Promise.resolve()
}

setAppName(props.appName)
init({
  onFolderFetch: props.onFolderFetch,
  onFolderCreate: props.onFolderCreate,
  onFolderUpdate: props.onFolderUpdate,
  onFolderDelete: props.onFolderDelete,
  onFoldersDelete: deleteFolders,
  onFileCreate: props.onFileCreate,
  onFileUpdate: props.onFileUpdate,
  onFileOpen: props.onFileOpen,
  onFileDelete: props.onFileDelete,
  onFilesDelete: deleteFiles,
  onFileFetch: props.onFileFetch,
  onFilesList: props.onFilesList,
  onItemsMove: props.onItemsMove,
  onFavoriteCreate: props.onFavoriteCreate,
  onFavoriteDelete: props.onFavoriteDelete,
  onFavoritesDelete: deleteFavorites,
  onFavoritesFetch: props.onFavoritesFetch,
  onRecentFilesFetch: props.onRecentFilesFetch,
  onSelectDeviceFile: props.onSelectDeviceFile,
  onFolderList: props.onFolderList,
})

defineExpose({
  onUploadComplete: onUploadFilesRefetchFolder,
  onFavoritesChanged: setFavorites,
  onUploadClearCompletedAndFailed: clearCompletedAndFailed,
  setSelectedSection,
  selectedSection,
  isUploadQueueBusy: isQueueBusy,
  currentFolderId,
  setCurrentFolder,
  folderItemsFetcher,
  recentFilesFetcher,
  favoritesFetcher,
  refreshSelectedFolderDetails,
})

onBeforeUnmount(() => {
  clearTimeouts()
  resetApiStore()
})

const onResize = debounce(() => {
  isMobileView.value = window.innerWidth < 1077
}, 100)

onMounted(() => {
  onResize()
  window.addEventListener('resize', onResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
})
</script>

<style scoped lang="scss">
.browser {
  background-color: v-bind('themeVars.primary6');
  border-top-left-radius: 16px;
}
</style>
